import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { useBankers } from "@/hooks/useBankers";
import { useLeads } from "@/hooks/useLeads";
import { useTasks } from "@/hooks/useTasks";
import { useAppConfig } from "@/hooks/useAppConfig";
import { INDIAN_CITIES } from "@/lib/constants";
import { Building2, Plus, Upload, Share2, Edit, Trash2, Search, Download, Phone, MapPin, Briefcase, Users, Bell } from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { SMContact } from "@/types";
import { exportBankers, generateBankerTemplate, parseExcelFile } from "@/services/excelExportService";
import { searchItems } from "@/lib/searchUtils";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface BankerManagementProps {
  hideAdd?: boolean;
  hideDelete?: boolean;
}

const DESIGNATION_OPTIONS = [
  'National Head',
  'Zonal Manager',
  'Regional Manager',
  'Area Sales Manager',
  'Sales Manager',
  'Other',
];

export function BankerManagement({ hideAdd = false, hideDelete = false }: BankerManagementProps) {
  const { bankers, loading, addBanker, updateBanker, deleteBanker } = useBankers();
  const { leads } = useLeads();
  const { tasks } = useTasks();
  const { config } = useAppConfig();

  // Terminal statuses for leads (not considered "current")
  const terminalStatuses = ['closed', 'removed', 'rejected', 'disbursed', 'payout_released'];
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editingBanker, setEditingBanker] = useState<SMContact | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [bulkImportOpen, setBulkImportOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bankerToDelete, setBankerToDelete] = useState<SMContact | null>(null);
  const [customDesignation, setCustomDesignation] = useState('');
  const [isOtherDesignation, setIsOtherDesignation] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    bank: "",
    phone: "",
    email: "",
    region: "",
    designation: "",
    products: [] as string[],
  });

  const filteredBankers = searchItems(
    searchQuery,
    bankers,
    ['name', 'phone', 'bank', 'region', 'email', 'designation', 'remark', 'products']
  );

  const handleBulkImport = async (file: File) => {
    try {
      // TODO: Parse Excel file and import bankers in bulk
      toast.success("Bulk import functionality coming soon");
      setBulkImportOpen(false);
    } catch (error) {
      toast.error("Failed to import bankers");
    }
  };

  const handleShareBankers = () => {
    if (filteredBankers.length === 0) {
      toast.error("No bankers to export");
      return;
    }
    try {
      exportBankers(filteredBankers as any);
      toast.success(`Exported ${filteredBankers.length} bankers to Excel`);
    } catch (error) {
      toast.error("Failed to export bankers");
      console.error("Export error:", error);
    }
  };

  const handleExportTemplate = () => {
    try {
      generateBankerTemplate();
      toast.success("Template downloaded successfully");
    } catch (error) {
      toast.error("Failed to generate template");
      console.error("Template error:", error);
    }
  };

  const confirmDelete = (banker: SMContact) => {
    setBankerToDelete(banker);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!bankerToDelete) return;
    
    try {
      await deleteBanker(bankerToDelete.id);
      toast.success("Banker deleted successfully");
    } catch (error) {
      toast.error("Failed to delete banker");
    } finally {
      setDeleteDialogOpen(false);
      setBankerToDelete(null);
    }
  };

  const handleAddNew = () => {
    setEditingBanker(null);
    setFormData({
      name: "",
      bank: "",
      phone: "",
      email: "",
      region: "",
      designation: "",
      products: [],
    });
    setCustomDesignation('');
    setIsOtherDesignation(false);
    setSheetOpen(true);
  };

  const handleEdit = (banker: SMContact) => {
    setEditingBanker(banker);
    const isCustom = !!(banker.designation && !DESIGNATION_OPTIONS.includes(banker.designation));
    setFormData({
      name: banker.name,
      bank: banker.bank,
      phone: banker.phone,
      email: banker.email || "",
      region: banker.region || "",
      designation: banker.designation || "",
      products: banker.products || [],
    });
    setIsOtherDesignation(isCustom);
    setCustomDesignation(isCustom ? banker.designation || '' : '');
    setSheetOpen(true);
  };

  const handleSaveBanker = async () => {
    if (!formData.name.trim() || !formData.bank.trim() || !formData.phone.trim()) {
      toast.error("Name, bank, and phone are required");
      return;
    }

    try {
      if (editingBanker) {
        await updateBanker(editingBanker.id, formData);
        toast.success("Banker updated successfully");
      } else {
        await addBanker(formData as Omit<SMContact, 'id'>);
        toast.success("Banker added successfully");
      }
      setSheetOpen(false);
      setEditingBanker(null);
    } catch (error) {
      toast.error(editingBanker ? "Failed to update banker" : "Failed to add banker");
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Banker Management
          </CardTitle>
          <div className="flex gap-2 flex-wrap">
            {!hideAdd && (
              <Button size="sm" variant="outline" onClick={() => setBulkImportOpen(true)}>
                <Upload className="h-4 w-4 mr-2" />
                Bulk Import
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={handleShareBankers}>
              <Share2 className="h-4 w-4 mr-2" />
              Export
            </Button>
            {!hideAdd && (
              <Button size="sm" onClick={handleAddNew}>
                <Plus className="h-4 w-4 mr-2" />
                Add Banker
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Search Filter */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search bankers: name, phone, bank, region, email, designation..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Banker List */}
        {loading ? (
          <div className="space-y-2">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-20 bg-muted/50 rounded-lg animate-pulse" />
            ))}
          </div>
        ) : filteredBankers.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Building2 className="h-12 w-12 mx-auto mb-2 opacity-50" />
            <p>No bankers found</p>
          </div>
        ) : (
          <>
            {/* Mobile Card View */}
            <div className="block md:hidden space-y-2 max-h-[400px] overflow-y-auto">
              {filteredBankers.map((banker, index) => (
                <motion.div
                  key={banker.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="border rounded-lg p-3 active:bg-muted/50 tap-highlight-green"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <div className="flex flex-col items-center gap-1 flex-shrink-0">
                        <Avatar className="h-9 w-9">
                          <AvatarFallback className="bg-blue-500 text-white text-xs font-semibold">
                            {banker.name.substring(0, 2).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        {(() => {
                          const bankerLeads = leads.filter(l => l.smContactId === banker.id && !terminalStatuses.includes(l.status)).length;
                          const bankerReminders = tasks.filter(t => t.smContactId === banker.id && !t.completed).length;
                          return (bankerLeads > 0 || bankerReminders > 0) ? (
                            <div className="flex flex-col items-center gap-0.5">
                              {bankerLeads > 0 && (
                                <div className="flex items-center gap-1 text-xs text-accent">
                                  <Users className="h-3.5 w-3.5" />
                                  <span className="font-semibold">{bankerLeads}</span>
                                </div>
                              )}
                              {bankerReminders > 0 && (
                                <div className="flex items-center gap-1 text-xs text-orange-500">
                                  <Bell className="h-3.5 w-3.5" />
                                  <span className="font-semibold">{bankerReminders}</span>
                                </div>
                              )}
                            </div>
                          ) : null;
                        })()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{banker.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{banker.phone}</p>
                      </div>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <Button 
                        size="icon" 
                        variant="ghost" 
                        className="h-9 w-9"
                        onClick={() => window.open(`tel:${banker.phone}`, '_self')}
                      >
                        <Phone className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="secondary" className="h-8 w-8 p-0 rounded-full" onClick={() => handleEdit(banker)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      {!hideDelete && (
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0 rounded-full" onClick={() => confirmDelete(banker)}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 mb-2">
                    <Badge variant="outline" className="text-[10px]">
                      {banker.bank}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs pt-2 border-t">
                    {banker.region && (
                      <div>
                        <p className="text-muted-foreground mb-0.5">Region</p>
                        <div className="flex items-center gap-1">
                          <MapPin className="h-3 w-3 text-muted-foreground" />
                          <p className="font-medium truncate">{banker.region}</p>
                        </div>
                      </div>
                    )}
                    {banker.designation && (
                      <div>
                        <p className="text-muted-foreground mb-0.5">Designation</p>
                        <div className="flex items-center gap-1">
                          <Briefcase className="h-3 w-3 text-muted-foreground" />
                          <p className="font-medium truncate">{banker.designation}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Desktop List View */}
            <div className="hidden md:block space-y-2 max-h-[400px] overflow-y-auto">
              {filteredBankers.map((banker, index) => (
                <motion.div
                  key={banker.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-medium">{banker.name}</p>
                      <Badge variant="outline" className="text-[10px]">
                        {banker.bank}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-muted-foreground">
                      <span>{banker.phone}</span>
                      {banker.region && (
                        <>
                          <span>•</span>
                          <span>{banker.region}</span>
                        </>
                      )}
                      {banker.designation && (
                        <>
                          <span>•</span>
                          <span>{banker.designation}</span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant="secondary"
                      className="h-8 w-8 p-0 rounded-full"
                      onClick={() => handleEdit(banker)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    {!hideDelete && (
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => confirmDelete(banker)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </>
        )}

        {/* Add/Edit Banker Sheet */}
        <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
          <SheetContent side="bottom" className="h-[90vh] rounded-t-3xl overflow-y-auto">
            <SheetHeader className="pb-4">
              <SheetTitle className="text-xl font-bold">
                {editingBanker ? "Edit Banker" : "Add New Banker"}
              </SheetTitle>
            </SheetHeader>
            
            <div className="space-y-4 pb-8">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Name *</Label>
                <Input 
                  value={formData.name} 
                  onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))}
                  placeholder="Ex - Rajesh Kumar"
                  className="h-12 placeholder:text-muted-foreground/50"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium">Bank *</Label>
                <SearchableSelect
                  options={config.banks.map(bank => ({ value: bank, label: bank }))}
                  value={formData.bank}
                  onValueChange={(v) => setFormData(p => ({ ...p, bank: v }))}
                  placeholder="Select bank"
                  searchPlaceholder="Search banks..."
                  emptyText="No bank found."
                />
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium">Phone *</Label>
                <div className="flex">
                  <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-input bg-muted text-sm text-muted-foreground">+91</span>
                  <Input 
                    value={formData.phone} 
                    onChange={(e) => setFormData(p => ({ ...p, phone: e.target.value.replace(/\D/g, '') }))}
                    placeholder="Ex - 9876543210"
                    type="tel"
                    maxLength={10}
                    className="h-12 rounded-l-none placeholder:text-muted-foreground/50"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium">Email</Label>
                <Input 
                  value={formData.email} 
                  onChange={(e) => setFormData(p => ({ ...p, email: e.target.value }))}
                  placeholder="Ex - name@email.com"
                  type="email"
                  className="h-12 placeholder:text-muted-foreground/50"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium">Region</Label>
                <SearchableSelect
                  options={INDIAN_CITIES.map(city => ({ value: city, label: city }))}
                  value={formData.region}
                  onValueChange={(v) => setFormData(p => ({ ...p, region: v }))}
                  placeholder="Select city"
                  searchPlaceholder="Search cities..."
                  emptyText="No city found."
                />
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium">Designation</Label>
                <Select 
                  value={isOtherDesignation ? 'Other' : (DESIGNATION_OPTIONS.includes(formData.designation) ? formData.designation : '')}
                  onValueChange={(value) => {
                    if (value === 'Other') {
                      setIsOtherDesignation(true);
                      setCustomDesignation('');
                      setFormData(p => ({ ...p, designation: '' }));
                    } else {
                      setIsOtherDesignation(false);
                      setCustomDesignation('');
                      setFormData(p => ({ ...p, designation: value }));
                    }
                  }}
                >
                  <SelectTrigger className="h-12">
                    <SelectValue placeholder="Select designation" />
                  </SelectTrigger>
                  <SelectContent>
                    {DESIGNATION_OPTIONS.map(d => (
                      <SelectItem key={d} value={d}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {/* Custom designation field - separate row when "Other" is selected */}
              {isOtherDesignation && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Custom Designation</Label>
                  <Input
                    autoFocus
                    value={customDesignation}
                    onChange={(e) => { setCustomDesignation(e.target.value); setFormData(p => ({ ...p, designation: e.target.value })); }}
                    placeholder="Enter custom designation"
                    className="h-12 placeholder:text-muted-foreground/50"
                  />
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <Button 
                  variant="outline" 
                  className="flex-1 h-12" 
                  onClick={() => setSheetOpen(false)}
                >
                  Cancel
                </Button>
                <Button 
                  className="flex-1 h-12" 
                  onClick={handleSaveBanker}
                >
                  {editingBanker ? "Update" : "Add"} Banker
                </Button>
              </div>
            </div>
          </SheetContent>
        </Sheet>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Banker</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete {bankerToDelete?.name}? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Bulk Import Sheet */}
        <Sheet open={bulkImportOpen} onOpenChange={setBulkImportOpen}>
          <SheetContent side="bottom" className="h-[50vh]">
            <SheetHeader>
              <SheetTitle>Bulk Import Bankers</SheetTitle>
            </SheetHeader>
            <div className="mt-6 space-y-4">
              <div className="border-2 border-dashed rounded-lg p-6 text-center">
                <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm text-muted-foreground mb-4">
                  Upload Excel file with banker data (.xlsx, .xls, .csv)
                </p>
                <Input
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={(e) => {
                    if (e.target.files?.[0]) {
                      handleBulkImport(e.target.files[0]);
                    }
                  }}
                />
              </div>
              <Button className="w-full" variant="outline" onClick={handleExportTemplate}>
                <Download className="h-4 w-4 mr-2" />
                Download Template
              </Button>
            </div>
          </SheetContent>
        </Sheet>
      </CardContent>
    </Card>
  );
}
